import { useEffect, useRef, useState } from 'react'
import { Camera, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { apiService } from '@/service/api'
import { probeImage } from '@/utils/imageDimensions'
import { useAppStore } from '@/store'
import { USER_AVATAR_GRADIENT_CLASS } from '@/utils/userUtils'
import { cn } from '@/lib/utils'

/**
 * Stored avatars are square, so the crop has to be too — a non-square source
 * centre-cropped to its shorter side, which is what every avatar UI does and
 * what stops a portrait photo rendering as a squashed circle.
 */
const AVATAR_PX = 256

/**
 * Refuse before uploading rather than after. The server caps at 5MB too, but a
 * 40MB original would be read into a canvas first, and that is where a phone
 * photo actually hurts — the browser tab, not the request.
 */
const MAX_SOURCE_BYTES = 20 * 1024 * 1024

/**
 * Pixel ceiling, checked from the file header BEFORE anything decodes.
 *
 * Bytes do not bound pixels, and that is the gap this closes: PNG and WebP are
 * lossless, so a few hundred KB can describe 30000×30000 — 900 megapixels, and
 * ~3.6GB once `createImageBitmap` expands it to RGBA. The byte cap above lets
 * that straight through, and the decode happens before any crop can shrink it,
 * so a tab can die before the 256px output exists.
 *
 * 60MP is ~240MB decoded: comfortably above any real camera (a 48MP phone photo
 * is 8000×6000) and far below the pathological case. Chosen so no legitimate
 * photo is refused — the number is a memory budget, not a taste judgement.
 */
const MAX_SOURCE_PIXELS = 60_000_000

/**
 * Centre-crop to a square and downscale, entirely in the browser.
 *
 * Done client-side on purpose: `backend/web` has no image library (no `sharp`,
 * no `jimp`), and adding one to resize an avatar is a lot of dependency for a
 * job `canvas` already does. It also means what we store is ~30-60KB rather
 * than whatever came off the phone.
 *
 * webp with a JPEG fallback: `toBlob` is allowed to hand back null for a type it
 * cannot encode, and a silent null here would look like a hung button.
 */
async function toSquareAvatarBlob(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  try {
    const side = Math.min(bitmap.width, bitmap.height)
    const canvas = document.createElement('canvas')
    canvas.width = AVATAR_PX
    canvas.height = AVATAR_PX
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas is unavailable')
    ctx.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      AVATAR_PX,
      AVATAR_PX,
    )
    const blob =
      (await canvasToBlob(canvas, 'image/webp')) ?? (await canvasToBlob(canvas, 'image/jpeg'))
    if (!blob) throw new Error('Could not encode the image')
    return blob
  } finally {
    bitmap.close()
  }
}

const canvasToBlob = (canvas: HTMLCanvasElement, type: string): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, 0.9))

/**
 * The profile-photo control (FS-693).
 *
 * Sits in the Settings → Profile card. The circle is the click target with a
 * camera scrim on hover — the Gmail affordance Pegah pointed at — but there is
 * also a plain button next to it, because a hover-only control is invisible to
 * anyone who does not happen to mouse over their own avatar.
 *
 * Precedence is resolved in `authSessionSlice`, not here: `user.picture` is
 * already "the avatar to render". This component only needs to know whether the
 * current one came from an upload (`uploadedAvatarUrl`) so it can offer Remove
 * and name the source otherwise.
 */
const ProfilePhotoField = () => {
  const { t } = useTranslation()
  const user = useAppStore((s) => s.user)
  const uploadedAvatarUrl = useAppStore((s) => s.uploadedAvatarUrl)
  const setUploadedAvatar = useAppStore((s) => s.setUploadedAvatar)

  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  // Without this the browser paints its broken-image placeholder — a question
  // mark in Safari — when the photo URL stops resolving, which is what happens
  // after removing the photo on the Google account the claim came from. Reset
  // on the source so a fresh upload or a refreshed claim is retried.
  const [photoFailed, setPhotoFailed] = useState(false)
  const photoSource = user?.picture
  useEffect(() => {
    setPhotoFailed(false)
  }, [photoSource])
  const photo = photoFailed ? undefined : photoSource

  const initial =
    user?.given_name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'

  const handlePick = async (file: File | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error(t('common:settings.photo.not_an_image', { defaultValue: 'Choose an image file.' }))
      return
    }
    if (file.size > MAX_SOURCE_BYTES) {
      toast.error(t('common:settings.photo.too_large', { defaultValue: 'That image is too large.' }))
      return
    }
    // Three outcomes, and the middle one is the point: a file whose bytes say
    // it IS one of the accepted formats but whose dimensions could not be read
    // is REFUSED, not waved through — we know what it is and cannot vouch for
    // its size. Only genuinely unrecognised bytes pass unchecked, and the
    // server's allowlist rejects those anyway.
    const probe = await probeImage(file)
    const overBudget =
      probe.kind === 'sized' && probe.width * probe.height > MAX_SOURCE_PIXELS
    if (overBudget || probe.kind === 'unsized') {
      toast.error(
        t('common:settings.photo.too_many_pixels', {
          defaultValue: 'That image is too big to resize in the browser. Try a smaller one.',
        }),
      )
      return
    }

    setBusy(true)
    try {
      const blob = await toSquareAvatarBlob(file)
      await apiService.uploadMyAvatar(blob)
      // Show the bytes we just sent rather than re-fetching them.
      setUploadedAvatar(blob)
      toast.success(t('common:settings.photo.updated', { defaultValue: 'Profile photo updated.' }))
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('common:settings.photo.failed', { defaultValue: 'Could not update your photo.' }),
      )
    } finally {
      setBusy(false)
      // Clear the input so picking the SAME file again still fires a change.
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleRemove = async () => {
    setBusy(true)
    try {
      await apiService.deleteMyAvatar()
      setUploadedAvatar(null)
      toast.success(t('common:settings.photo.removed', { defaultValue: 'Profile photo removed.' }))
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('common:settings.photo.remove_failed', {
              defaultValue: 'Could not remove your photo.',
            }),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-4">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void handlePick(e.target.files?.[0])}
      />

      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        aria-label={t('common:settings.photo.change', { defaultValue: 'Change profile photo' })}
        className={cn(
          'group relative size-24 shrink-0 overflow-hidden rounded-full',
          'flex items-center justify-center text-2xl font-normal text-white',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400',
          busy ? 'cursor-wait' : 'cursor-pointer',
          USER_AVATAR_GRADIENT_CLASS,
        )}
      >
        {photo && (
          <img
            src={photo}
            alt=""
            referrerPolicy="no-referrer"
            className="size-full object-cover"
            draggable={false}
            onError={() => setPhotoFailed(true)}
          />
        )}
        {!photo && initial}

        {/* The scrim is the hover affordance; it also shows while busy so the
            spinner has something to sit on. */}
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-black/45 transition-opacity',
            busy ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
        >
          {busy ? (
            <Loader2 className="size-6 animate-spin text-white" />
          ) : (
            <Camera className="size-6 text-white" />
          )}
        </span>
      </button>

      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <IntuiButton variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}>
            {uploadedAvatarUrl
              ? t('common:settings.photo.change_action', { defaultValue: 'Change photo' })
              : t('common:settings.photo.upload_action', { defaultValue: 'Upload photo' })}
          </IntuiButton>
          {uploadedAvatarUrl && (
            <IntuiButton variant="ghost" size="sm" disabled={busy} onClick={() => void handleRemove()}>
              {t('common:settings.photo.remove_action', { defaultValue: 'Remove' })}
            </IntuiButton>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {uploadedAvatarUrl
            ? t('common:settings.photo.hint_uploaded', {
                defaultValue: 'PNG or JPG. Removing yours restores the photo from your sign-in.',
              })
            : user?.ssoPicture
              ? t('common:settings.photo.hint_from_sso', {
                  defaultValue: 'Imported from your sign-in account. Upload one to replace it.',
                })
              : t('common:settings.photo.hint_none', {
                  defaultValue: 'PNG or JPG, square works best.',
                })}
        </p>
      </div>
    </div>
  )
}

export default ProfilePhotoField
